// ABOUTME: The chevron-driven, scrollbar-less scroll the rail panels use — extracted from IconRail
// ABOUTME: (SPEC-53) so the collection piece card scrolls by exactly the same code path.

import React from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Scroll an element by exactly one visible page.
 *
 * Module-level and named so the agent's `scroll_panel` effect and the guest's chevron click are
 * literally the same code path — they were about to become two `el.scrollBy` call sites with the
 * same arithmetic, which is the shape that drifts (2026-09-01).
 *
 * The element's own `clientHeight` is the real page, preferred over the caller's `pageHeight`
 * because the box is usually a shrinkable flex item: on a short viewport its visible height is
 * whatever is left once its neighbours have taken theirs. `pageHeight` stays as the fallback for
 * jsdom, which reports 0 for every measurement.
 *
 * @param {HTMLElement|null} el the scrolling element
 * @param {'up'|'down'} direction
 * @param {number} pageHeight one visible page in px, used if unmeasurable
 * @param {ScrollBehavior} [behavior]
 */
export function scrollGridByPage(el, direction, pageHeight, behavior = 'smooth') {
  if (!el) return;
  const page = el.clientHeight || pageHeight;
  el.scrollBy({ top: direction === 'up' ? -page : page, behavior });
}

/**
 * One read of an element's scroll position, in the shape both the chevrons and the upstream
 * `panel_scroll` report consume. `overflowing` is MEASURED, never inferred from a row count: the
 * wire contract's `scrollable` means "is there anywhere to scroll to".
 *
 * @param {HTMLElement|null} el
 * @returns {{ overflowing: boolean, up: boolean, down: boolean }}
 */
export function readScrollMetrics(el) {
  const overflowing = !!el && el.scrollHeight > el.clientHeight + 4;
  const up = overflowing && el.scrollTop > 4;
  const down = overflowing && el.scrollTop + el.clientHeight < el.scrollHeight - 4;
  return { overflowing, up, down };
}

/**
 * A small scroll affordance over a list — shown only on the edge it points toward has more
 * content, so it never lingers once the guest (or the agent) has scrolled all the way that way.
 *
 * A real `<button>` since 2026-09-01. It was a `pointer-events-none aria-hidden` div, which meant
 * the panel could only be scrolled by the agent (`scroll_panel`) or by a wheel/trackpad gesture — a
 * guest who saw the chevron and clicked it got nothing, and a keyboard-only guest had no way at all.
 * It keeps the same 28px disc and bounce; only its semantics and hit behaviour changed.
 */
export const ScrollHint = React.forwardRef(function ScrollHint({ direction, onClick, size = 28, overhang = 10 }, ref) {
  const Icon = direction === 'up' ? ChevronUp : ChevronDown;
  return (
    <button
      ref={ref}
      type="button"
      aria-label={direction === 'up' ? 'Scroll up' : 'Scroll down'}
      onClick={onClick}
      className={`absolute left-1/2 -translate-x-1/2 ${direction === 'up' ? 'top-0' : 'bottom-0'} z-10 flex items-center justify-center rounded-full bg-reel-chrome-panel/90 backdrop-blur-sm shadow-md animate-bounce hover:bg-reel-chrome-panel transition-colors`}
      // `pointerEvents` inline for the jsdom reason documented on the rail root: a Tailwind utility
      // never resolves through `getComputedStyle` with no stylesheet loaded, so user-event's
      // pointer-events walk would find the portal's inline `none` and refuse the click.
      style={{ pointerEvents: 'auto', width: size, height: size, [direction === 'up' ? 'top' : 'bottom']: -overhang }}
    >
      <Icon size={Math.round(size * (16 / 28))} aria-hidden="true" className="text-reel-chrome-textPrimary" />
    </button>
  );
});
